import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Typography,
  Spin,
  Empty,
  Tag,
  message,
  Divider,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  AimOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { PatientReviApi } from "../../../../api/revi/patientApi";
import { useAuth } from "../../../../hooks";
import "./TherapistPatientAnamnesisTab.scss";

const { Text, Title } = Typography;
const { TextArea } = Input;
const api = new PatientReviApi();

// Las secciones son fijas: `key` es el campo de la BD y no se traduce nunca.
// Lo que si es interfaz son la etiqueta y el texto de ayuda, que se resuelven
// en el render con `t`. LO QUE ESCRIBE EL TERAPEUTA DENTRO NO SE TOCA.
const SECTIONS = [
  {
    key: "referral_info",
    labelKey: "anamnesis.secciones.referral_info.etiqueta",
    icon: <InfoCircleOutlined />,
    placeholderKey: "anamnesis.secciones.referral_info.placeholder",
    rows: 3,
  },
  {
    key: "diagnosis",
    labelKey: "anamnesis.secciones.diagnosis.etiqueta",
    icon: <MedicineBoxOutlined />,
    placeholderKey: "anamnesis.secciones.diagnosis.placeholder",
    rows: 2,
  },
  {
    key: "medical_history",
    labelKey: "anamnesis.secciones.medical_history.etiqueta",
    icon: <FileTextOutlined />,
    placeholderKey: "anamnesis.secciones.medical_history.placeholder",
    rows: 3,
  },
  {
    key: "medications",
    labelKey: "anamnesis.secciones.medications.etiqueta",
    icon: <ExperimentOutlined />,
    placeholderKey: "anamnesis.secciones.medications.placeholder",
    rows: 2,
  },
  {
    key: "functional_status",
    labelKey: "anamnesis.secciones.functional_status.etiqueta",
    icon: <HeartOutlined />,
    placeholderKey: "anamnesis.secciones.functional_status.placeholder",
    rows: 3,
  },
  {
    key: "observations",
    labelKey: "anamnesis.secciones.observations.etiqueta",
    icon: <SolutionOutlined />,
    placeholderKey: "anamnesis.secciones.observations.placeholder",
    rows: 3,
  },
  {
    key: "goals",
    labelKey: "anamnesis.secciones.goals.etiqueta",
    icon: <AimOutlined />,
    placeholderKey: "anamnesis.secciones.goals.placeholder",
    rows: 3,
  },
];

function generatePatientCode(patientId) {
  if (!patientId) return "REV-X-000";
  const tail = String(patientId).slice(-3).toUpperCase();
  return `REV-X-${tail}`;
}

function hasClinicalContent(info) {
  if (!info) return false;
  return SECTIONS.some(
    (s) => typeof info[s.key] === "string" && info[s.key].trim().length > 0,
  );
}

export function TherapistPatientAnamnesisTab({
  patient,
  onClinicalInfoUpdate,
}) {
  // "Guardar", "Cancelar" y "Editar" salen del bloque comun.
  const { t } = useTranslation(["patient", "common"]);
  const { fechaHora } = useFormatos();

  const { accessToken } = useAuth();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const clinicalInfo = patient?.clinical_info || null;
  const hasContent = useMemo(
    () => hasClinicalContent(clinicalInfo),
    [clinicalInfo],
  );
  const patientCode = useMemo(
    () => generatePatientCode(patient?._id),
    [patient?._id],
  );

  useEffect(() => {
    if (clinicalInfo) {
      const initial = {};
      SECTIONS.forEach((s) => {
        initial[s.key] = clinicalInfo[s.key] || "";
      });
      setForm(initial);
    } else {
      const empty = {};
      SECTIONS.forEach((s) => {
        empty[s.key] = "";
      });
      setForm(empty);
    }
  }, [clinicalInfo]);

  const onFieldChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onSave = useCallback(async () => {
    if (!patient?._id || !accessToken) return;

    try {
      setSaving(true);
      const res = await api.updateClinicalInfo(accessToken, patient._id, form);
      message.success(t("anamnesis.guardada"));

      if (typeof onClinicalInfoUpdate === "function") {
        onClinicalInfoUpdate(res.clinical_info);
      }

      setEditing(false);
    } catch (e) {
      console.error(e);
      message.error(e?.message || t("anamnesis.errorGuardar"));
    } finally {
      setSaving(false);
    }
  }, [patient?._id, accessToken, form, onClinicalInfoUpdate, t]);

  const onCancel = useCallback(() => {
    if (clinicalInfo) {
      const initial = {};
      SECTIONS.forEach((s) => {
        initial[s.key] = clinicalInfo[s.key] || "";
      });
      setForm(initial);
    }
    setEditing(false);
  }, [clinicalInfo]);

  const updatedBy = clinicalInfo?.updated_by;
  const updatedAt = clinicalInfo?.updated_at;
  const updatedByName =
    updatedBy && typeof updatedBy === "object"
      ? `${updatedBy.firstname || ""} ${updatedBy.lastname || ""}`.trim()
      : null;

  return (
    <div className="revi-anamnesis">
      <div className="revi-anamnesis__header">
        <div className="revi-anamnesis__header-left">
          <div className="revi-anamnesis__header-title">
            <Title level={5} style={{ margin: 0 }}>
              {t("anamnesis.titulo")}
            </Title>
            <Tag color={hasContent ? "green" : "default"}>
              {hasContent
                ? t("anamnesis.estadoActivo")
                : t("anamnesis.estadoSinDatos")}
            </Tag>
          </div>
          <Text type="secondary">
            {t("anamnesis.idEtiqueta")} {patientCode}
          </Text>
        </div>

        <div className="revi-anamnesis__header-actions">
          {editing ? (
            <>
              <Button
                icon={<CloseOutlined />}
                onClick={onCancel}
                disabled={saving}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onSave}
                loading={saving}
              >
                {t("common:actions.save")}
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={() => setEditing(true)}
            >
              {hasContent ? t("common:actions.edit") : t("anamnesis.registrar")}
            </Button>
          )}
        </div>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {!hasContent && !editing ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("anamnesis.sinDatos")}
        >
          <Button
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
          >
            {t("anamnesis.registrar")}
          </Button>
        </Empty>
      ) : (
        <div className="revi-anamnesis__sections">
          {SECTIONS.map((section) => {
            const value = form[section.key] || "";
            const hasValue = value.trim().length > 0;

            if (!editing && !hasValue) return null;

            return (
              <div key={section.key} className="revi-anamnesis__section">
                <div className="revi-anamnesis__section-header">
                  <span className="revi-anamnesis__section-icon">
                    {section.icon}
                  </span>
                  <Text strong className="revi-anamnesis__section-label">
                    {t(section.labelKey)}
                  </Text>
                </div>

                {editing ? (
                  <TextArea
                    value={value}
                    onChange={(e) => onFieldChange(section.key, e.target.value)}
                    placeholder={t(section.placeholderKey)}
                    rows={section.rows}
                    className="revi-anamnesis__textarea"
                    autoSize={{ minRows: section.rows, maxRows: 8 }}
                  />
                ) : (
                  <div className="revi-anamnesis__section-content">
                    <Text>{value}</Text>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasContent && updatedAt && (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <div className="revi-anamnesis__footer">
            {/*
              Dos frases enteras, no una montada a trozos: en frances "par
              {{autor}}" no cae en el mismo sitio y el "de" cambia con la
              inicial del nombre.
            */}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {updatedByName
                ? t("anamnesis.ultimaActualizacionPor", {
                    fecha: fechaHora(updatedAt),
                    autor: updatedByName,
                  })
                : t("anamnesis.ultimaActualizacion", {
                    fecha: fechaHora(updatedAt),
                  })}
            </Text>
          </div>
        </>
      )}

      {saving && (
        <div className="revi-anamnesis__overlay">
          <Spin />
        </div>
      )}
    </div>
  );
}

export { hasClinicalContent };
